/**
 * Memberships & Plans (AM-08..13, BP-08). Plan catalog: list + create/edit/
 * deactivate over the `plans` table (RLS owner/manager). Money in minor units.
 */
import type { PlanType } from '@gym/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Layers, Pencil, Plus } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, IconButton } from '../../components/ui/Button.js';
import { EmptyState } from '../../components/ui/Feedback.js';
import { Field, Input, Select } from '../../components/ui/Field.js';
import { PageHeader } from '../../components/ui/Layout.js';
import { Modal } from '../../components/ui/Modal.js';
import { Money } from '../../components/ui/Money.js';
import { Chip, StatusPill } from '../../components/ui/StatusPill.js';
import { Table, TableSkeleton, TableWrap, TD, TH, THead, TR } from '../../components/ui/Table.js';
import { toast } from '../../components/ui/Toaster.js';
import { useAuth } from '../../lib/auth.js';
import { supabase } from '../../lib/supabase.js';

const PLAN_TYPES: PlanType[] = [
  'monthly',
  'quarterly',
  'annual',
  'unlimited',
  'off_peak',
  'session_pack',
  'day_pass',
  'punch_card',
  'class_pack',
  'pt_pack',
];

interface PlanRow {
  id: string;
  name: string;
  plan_type: PlanType;
  price: number;
  duration_days: number | null;
  session_credits: number | null;
  is_active: boolean;
  sort_order: number | null;
}

export function MembershipsPage(): ReactNode {
  const { t } = useTranslation();
  const { claims } = useAuth();
  const tenantId = claims?.tenant_id ?? '';
  const [editing, setEditing] = useState<PlanRow | null>(null);
  const [creating, setCreating] = useState(false);

  const plans = useQuery({
    queryKey: ['plans', tenantId],
    enabled: !!tenantId,
    queryFn: async (): Promise<PlanRow[]> => {
      const { data, error } = await supabase
        .from('plans')
        .select('id, name, plan_type, price, duration_days, session_credits, is_active, sort_order')
        .order('sort_order', { ascending: true, nullsFirst: false })
        .order('price', { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as PlanRow[];
    },
  });

  const rows = plans.data ?? [];

  return (
    <div className="grid gap-5">
      <PageHeader
        title={t('memberships.plansTitle')}
        subtitle={t('memberships.plansSubtitle')}
        actions={
          <Button
            variant="brand"
            icon={<Plus className="h-4 w-4" />}
            onClick={() => setCreating(true)}
          >
            {t('memberships.newPlan')}
          </Button>
        }
      />

      <div className="overflow-hidden rounded-xl border border-border bg-surface">
        <TableWrap>
          <Table>
            <THead>
              <tr>
                <TH>{t('memberships.planName')}</TH>
                <TH>{t('memberships.planType')}</TH>
                <TH numeric>{t('billing.amount')}</TH>
                <TH numeric>{t('memberships.duration')}</TH>
                <TH numeric>{t('memberships.credits')}</TH>
                <TH>{t('memberships.statusLabel')}</TH>
                <TH numeric aria-label={t('common.actions')} />
              </tr>
            </THead>
            {plans.isLoading ? (
              <TableSkeleton rows={6} cols={7} />
            ) : (
              <tbody>
                {rows.map((p) => (
                  <TR key={p.id}>
                    <TD className="font-medium">{p.name}</TD>
                    <TD>
                      <Chip tone="neutral">
                        {t(`plans.type.${p.plan_type}`, { defaultValue: p.plan_type })}
                      </Chip>
                    </TD>
                    <TD numeric>
                      <Money minor={p.price} />
                    </TD>
                    <TD numeric className="text-muted">
                      {p.duration_days
                        ? t('memberships.daysCount', { count: p.duration_days })
                        : '—'}
                    </TD>
                    <TD numeric className="text-muted">
                      {p.session_credits ?? '—'}
                    </TD>
                    <TD>
                      <StatusPill
                        status={p.is_active ? 'active' : 'cancelled'}
                        label={p.is_active ? t('common.active') : t('common.inactive')}
                      />
                    </TD>
                    <TD numeric>
                      <IconButton label={t('common.edit')} onClick={() => setEditing(p)}>
                        <Pencil className="h-4 w-4" />
                      </IconButton>
                    </TD>
                  </TR>
                ))}
              </tbody>
            )}
          </Table>
        </TableWrap>
        {!plans.isLoading && rows.length === 0 ? (
          <div className="p-6">
            <EmptyState
              icon={Layers}
              title={t('memberships.noPlans')}
              hint={t('memberships.noPlansHint')}
              action={
                <Button
                  variant="brand"
                  icon={<Plus className="h-4 w-4" />}
                  onClick={() => setCreating(true)}
                >
                  {t('memberships.newPlan')}
                </Button>
              }
            />
          </div>
        ) : null}
      </div>

      {(creating || editing) && (
        <PlanModal
          tenantId={tenantId}
          plan={editing}
          open={creating || !!editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function PlanModal({
  tenantId,
  plan,
  open,
  onClose,
}: {
  tenantId: string;
  plan: PlanRow | null;
  open: boolean;
  onClose: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState(plan?.name ?? '');
  const [planType, setPlanType] = useState<PlanType>(plan?.plan_type ?? 'monthly');
  const [priceMajor, setPriceMajor] = useState(plan ? String(plan.price / 100) : '');
  const [durationDays, setDurationDays] = useState(plan?.duration_days?.toString() ?? '');
  const [credits, setCredits] = useState(plan?.session_credits?.toString() ?? '');
  const [active, setActive] = useState(plan?.is_active ?? true);

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        name: name.trim(),
        plan_type: planType,
        price: Math.round(Number(priceMajor) * 100),
        duration_days: durationDays ? Number(durationDays) : null,
        session_credits: credits ? Number(credits) : null,
        is_active: active,
      };
      if (plan) {
        const { error } = await supabase
          .from('plans')
          .update({ ...payload, updated_at: new Date().toISOString() })
          .eq('id', plan.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('plans').insert({ ...payload, tenant_id: tenantId });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success(plan ? t('memberships.planUpdated') : t('memberships.planCreated'), name);
      void qc.invalidateQueries({ queryKey: ['plans'] });
      onClose();
    },
    onError: (e: Error) => toast.error(t('common.error'), e.message),
  });

  return (
    <Modal
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title={plan ? t('memberships.editPlan') : t('memberships.newPlan')}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => save.mutate()}
            loading={save.isPending}
            disabled={!name.trim() || !priceMajor}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        <Field label={t('memberships.planName')} required>
          {(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('memberships.planType')}>
            {(id) => (
              <Select
                id={id}
                value={planType}
                onChange={(e) => setPlanType(e.target.value as PlanType)}
              >
                {PLAN_TYPES.map((pt) => (
                  <option key={pt} value={pt}>
                    {t(`plans.type.${pt}`, { defaultValue: pt })}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={`${t('billing.amount')} (ден)`} required>
            {(id) => (
              <Input
                id={id}
                type="number"
                min="0"
                step="1"
                value={priceMajor}
                onChange={(e) => setPriceMajor(e.target.value)}
              />
            )}
          </Field>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('memberships.duration')} hint={t('memberships.durationHint')}>
            {(id) => (
              <Input
                id={id}
                type="number"
                min="0"
                value={durationDays}
                onChange={(e) => setDurationDays(e.target.value)}
              />
            )}
          </Field>
          <Field label={t('memberships.credits')} hint={t('memberships.creditsHint')}>
            {(id) => (
              <Input
                id={id}
                type="number"
                min="0"
                value={credits}
                onChange={(e) => setCredits(e.target.value)}
              />
            )}
          </Field>
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-fg">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
            className="h-4 w-4 accent-[rgb(var(--primary))]"
          />
          {t('memberships.activeForSale')}
        </label>
      </div>
    </Modal>
  );
}
